import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { MiniSparkline } from "@/components/Charts";
import { fmtCurrency, fmtSigned, fmtPct, fmtDate, gainClass } from "@/lib/format";
import { TXN_BADGE } from "@/lib/constants";
import { ArrowRight } from "lucide-react";

export const MOVER_MODES = [
    { key: "moves", label: "Biggest", desc: "Largest moves", empty: "No movers to show" },
    { key: "gainers", label: "Gainers", desc: "Biggest gainers", empty: "No gainers this period" },
    { key: "losers", label: "Losers", desc: "Biggest losers", empty: "No losers this period" },
];
export const MOVER_TFS = [
    { key: "1D", label: "Today", word: "today" },
    { key: "1W", label: "Week", word: "this week" },
    { key: "YTD", label: "YTD", word: "year to date" },
];
export const MOVER_COUNTS = [
    { key: 5, label: "Top 5" },
    { key: 10, label: "Top 10" },
    { key: 500, label: "All" },
];

function Toggle({ testId, options, value, onChange, prefix }) {
    return (
        <div className="inline-flex rounded-lg bg-muted p-0.5" data-testid={testId}>
            {options.map((o) => (
                <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${prefix}-${o.key}`}
                    className={`px-2 py-0.5 text-[11px] font-medium rounded-md transition-colors ${value === o.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function MoverRow({ m, mask, onNavigate }) {
    return (
        <button onClick={() => onNavigate(`/security/${m.ticker}`)}
            className="w-full flex items-center justify-between gap-2 py-1 px-1.5 rounded-md hover:bg-muted/50 transition-colors text-left"
            data-testid={`mover-row-${m.ticker}`}>
            <div className="min-w-0 flex-1">
                <div className="font-semibold text-xs num truncate">{m.ticker}</div>
                <div className="text-[10px] text-muted-foreground truncate">{m.name}</div>
            </div>
            <div className="hidden sm:block shrink-0" data-testid={`mover-spark-${m.ticker}`}>
                <MiniSparkline values={m.spark || []} color={(m.pct ?? 0) >= 0 ? "hsl(var(--gain))" : "hsl(var(--loss))"} width={56} height={20} />
            </div>
            <div className={`num text-xs font-semibold text-right w-[68px] shrink-0 ${gainClass(m.dollar)}`} data-testid={`mover-dollar-${m.ticker}`}>{m.dollar != null ? mask(fmtSigned(m.dollar)) : "—"}</div>
            <div className={`num text-xs font-semibold text-right w-[56px] shrink-0 ${gainClass(m.pct)}`}>{fmtPct(m.pct)}</div>
        </button>
    );
}

export function MoversCard({ mode, setMode, tf, setTf, count, setCount, sub, setSub, rows, loading, mask, onNavigate }) {
    const modeMeta = MOVER_MODES.find((m) => m.key === mode);
    const tfMeta = MOVER_TFS.find((t) => t.key === tf);
    return (
        <Card className="p-4 flex flex-col" data-testid="movers-card">
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <h2 className="font-heading text-base font-semibold">Top Movers <span className="text-xs font-normal text-muted-foreground">· {modeMeta?.desc.toLowerCase()} {tfMeta?.word}</span></h2>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                <SubPortfolioSelect value={sub} onChange={setSub} testId="mover-sub-select" className="h-7 w-[130px] text-xs" />
                <Toggle testId="mover-tf-toggle" prefix="mover-tf" options={MOVER_TFS} value={tf} onChange={setTf} />
                <Toggle testId="mover-count-toggle" prefix="mover-count" options={MOVER_COUNTS} value={count} onChange={setCount} />
                <Toggle testId="mover-mode-toggle" prefix="mover-mode" options={MOVER_MODES} value={mode} onChange={setMode} />
            </div>
            <div className="flex-1 overflow-y-auto max-h-[250px] -mx-1.5" data-testid="movers-list">
                {rows.map((m) => <MoverRow key={m.ticker} m={m} mask={mask} onNavigate={onNavigate} />)}
                {!loading && rows.length === 0 && <p className="text-sm text-muted-foreground py-4 px-1.5" data-testid="mover-empty">{modeMeta?.empty} {tfMeta?.word}.</p>}
            </div>
        </Card>
    );
}

export function RecentTransactionsCard({ txns, mask, onViewAll }) {
    return (
        <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold">Recent Transactions</h2>
                <Button variant="ghost" size="sm" onClick={onViewAll} data-testid="view-all-txns">All <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
            <div className="space-y-2">
                {(txns || []).slice(0, 6).map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${TXN_BADGE[t.transaction_type]}`}>{t.transaction_type}</Badge>
                            <span className="text-sm font-medium truncate num">{t.ticker || "Cash"}</span>
                        </div>
                        <div className="text-right">
                            <div className="num text-sm">{mask(fmtCurrency((t.quantity || 0) * (t.price || 0)))}</div>
                            <div className="text-[10px] text-muted-foreground num">{fmtDate(t.transaction_date)}</div>
                        </div>
                    </div>
                ))}
                {(txns?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground py-4">No transactions.</p>}
            </div>
        </Card>
    );
}
